import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Camera } from 'lucide-react';
import { ProductFormData } from '../../types/Product';
import { formatDateForInput } from '../../utils/formatters';
import { BarcodeScanner } from '../common/BarcodeScanner';

const requiredNumber = (msg: string) =>
  z.number({ message: 'Geçerli bir sayı girin' }).min(0, msg);

const productSchema = z
  .object({
    name: z.string().min(1, 'Ürün adı gereklidir'),
    color: z.string().optional(),
    quantity: requiredNumber('Miktar 0 veya daha büyük olmalıdır'),
    brand: z.string().optional(),
    code: z.string().optional(),
    purchaseDate: z.string().optional(),
    unitPrice: requiredNumber('Alış fiyatı 0 veya daha büyük olmalıdır'),
    retailPrice: requiredNumber('Satış fiyatı 0 veya daha büyük olmalıdır'),
    description: z.string().optional(),
  })
  .refine(
    (data) => data.unitPrice <= data.retailPrice,
    {
      path: ['unitPrice'],
      message: 'Alış fiyatı satış fiyatından yüksek olamaz',
    }
  );

interface ProductFormProps {
  initialData?: Partial<ProductFormData>;
  onSubmit: (data: ProductFormData) => void;
  loading?: boolean;
  formRef?: React.RefObject<HTMLFormElement | null>;
}

export const ProductForm: React.FC<ProductFormProps> = ({
  initialData,
  onSubmit,
  loading,
  formRef,
}) => {
  const [scannerOpen, setScannerOpen] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: initialData || {
      name: '',
      color: '',
      quantity: 0,
      brand: '',
      code: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      unitPrice: 0,
      retailPrice: 0,
      description: '',
    },
  });

  React.useEffect(() => {
    if (initialData?.purchaseDate) {
      setValue('purchaseDate', formatDateForInput(initialData.purchaseDate));
    }
  }, [initialData, setValue]);

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        {/* Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-foreground-secondary mb-2">
              Ürün Adı <span className="text-error">*</span>
            </label>
            <input
              id="name"
              type="text"
              {...register('name')}
              disabled={loading}
              className={`input ${errors.name ? 'border-error focus:ring-error' : ''}`}
            />
            {errors.name && (
              <p className="mt-1.5 text-xs text-error">{errors.name.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="color" className="block text-sm font-medium text-foreground-secondary mb-2">
              Renk
            </label>
            <input
              id="color"
              type="text"
              {...register('color')}
              disabled={loading}
              className="input"
            />
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="quantity" className="block text-sm font-medium text-foreground-secondary mb-2">
              Miktar <span className="text-error">*</span>
            </label>
            <input
              id="quantity"
              type="number"
              {...register('quantity', { valueAsNumber: true })}
              onWheel={(e) => e.currentTarget.blur()}
              disabled={loading}
              className={`input ${errors.quantity ? 'border-error focus:ring-error' : ''}`}
            />
            {errors.quantity && (
              <p className="mt-1.5 text-xs text-error">{errors.quantity.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="brand" className="block text-sm font-medium text-foreground-secondary mb-2">
              Marka
            </label>
            <input
              id="brand"
              type="text"
              {...register('brand')}
              disabled={loading}
              className="input"
            />
          </div>
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-foreground-secondary mb-2">
              Barkod
            </label>
            <div className="relative">
              <input
                id="code"
                type="text"
                {...register('code')}
                disabled={loading}
                className="input pr-10"
              />
              <button
                type="button"
                onClick={() => setScannerOpen(true)}
                disabled={loading}
                className="absolute inset-y-0 right-0 pr-3 flex items-center hover:opacity-70 transition-opacity"
                title="Barkod Tara"
              >
                <Camera className="w-4 h-4 text-accent" />
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="purchaseDate" className="block text-sm font-medium text-foreground-secondary mb-2">
              Alım Tarihi
            </label>
            <input
              id="purchaseDate"
              type="date"
              {...register('purchaseDate')}
              disabled={loading}
              className="input"
            />
          </div>
        </div>

        {/* Row 4 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="unitPrice" className="block text-sm font-medium text-foreground-secondary mb-2">
              Alış Fiyatı <span className="text-error">*</span>
            </label>
            <input
              id="unitPrice"
              type="number"
              step="0.01"
              {...register('unitPrice', { valueAsNumber: true })}
              onWheel={(e) => e.currentTarget.blur()}
              disabled={loading}
              className={`input ${errors.unitPrice ? 'border-error focus:ring-error' : ''}`}
            />
            {errors.unitPrice && (
              <p className="mt-1.5 text-xs text-error">{errors.unitPrice.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="retailPrice" className="block text-sm font-medium text-foreground-secondary mb-2">
              Satış Fiyatı <span className="text-error">*</span>
            </label>
            <input
              id="retailPrice"
              type="number"
              step="0.01"
              {...register('retailPrice', { valueAsNumber: true })}
              onWheel={(e) => e.currentTarget.blur()}
              disabled={loading}
              className={`input ${errors.retailPrice ? 'border-error focus:ring-error' : ''}`}
            />
            {errors.retailPrice && (
              <p className="mt-1.5 text-xs text-error">{errors.retailPrice.message}</p>
            )}
          </div>
        </div>

        {/* Row 5 */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-foreground-secondary mb-2">
            Açıklama
          </label>
          <textarea
            id="description"
            rows={4}
            {...register('description')}
            disabled={loading}
            className="input resize-none"
          />
        </div>
      </div>

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={(barcode) => {
          setValue('code', barcode, { shouldValidate: true });
          setScannerOpen(false);
        }}
      />
    </form>
  );
};
